import { Paper, Stack } from "@mui/material";
import { CostTableList } from "./CostTableList";
import { CostPlot } from "./CostPlot";
import { MultiSelect } from "../../common/inputs/multiSelect";
import { useEffect, useMemo, useState } from "react";
import { useTenantTranslation } from "hooks/formatters";
import { useLiteralListQueryParamState } from "hooks/useLiteralListQueryParamState";
import { CostTableSource, MassDenominator } from "src/store/api/generatedApi";
import { CostTabData, GroupedByCostSource } from "../types";
import { typeSafeObjectKeys } from "src/utils/typeSafeObjectKeys";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { ToggleSelector } from "../ToggleSelector";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";
import { useIsAdmin } from "hooks/settings";
import { DateRangeSelector } from "components/common/DateRangeSelector";
import { ExcelDownloadButton } from "components/common/ExcelDownloadButton";
import { useDateRange } from "contexts/DateRangeContext";
import { backendApi, downloadAsExcel } from "src/store/api/enhancedApi";
import { useTenant } from "hooks/settings";

interface CostTabProps {
  data: CostTabData;
}

export const CostTab = ({ data }: CostTabProps) => {
  const isAdmin = useIsAdmin();
  const { t } = useTenantTranslation();
  const tenant = useTenant();
  const availableTableSources = useMemo(() => typeSafeObjectKeys(data), [data]);
  const availableMassDenominators = useMemo(
    () => Object.values(data)[0]?.availableMassDenominators ?? [],
    [data]
  );

  const [selectedTableSource, setSelectedTableSource] =
    useStringQueryParamState(
      "tableSource",
      availableTableSources[0] ?? "charged_cost_timeseries"
    );

  const [selectedMassDenominator, setSelectedMassDenominator] =
    useStringQueryParamState<MassDenominator>("massDenominator", "charged");

  const selectedData = useMemo(
    () => data[selectedTableSource],
    [data, selectedTableSource]
  );

  const availableCostSources = selectedData.availableSources;

  const [selectedSources, setSelectedSources] =
    useLiteralListQueryParamState<string>({
      paramName: "costSources",
      permitted: availableCostSources,
      localStorageKey: `${tenant}.strategyDashboard.cost.selectedSources`,
      defaultValue: availableCostSources,
    });

  const visibleSources =
    selectedSources.length > 0 ? selectedSources : availableCostSources;

  const [selectedDate, setSelectedDate] = useState(() =>
    getDefaultSelectedDate(selectedData, selectedTableSource)
  );
  const availableTableSourceLabels = useMemo(
    () =>
      typeSafeObjectFromEntries(
        availableTableSources.map((source) => [source, source])
      ),
    [availableTableSources]
  );
  const availableMassDenominatorLabels = useMemo(
    () =>
      typeSafeObjectFromEntries(
        availableMassDenominators.map((massDenominator) => [
          massDenominator,
          `${massDenominator}Mass`,
        ])
      ),
    [availableMassDenominators]
  );

  useEffect(() => {
    // when one of the select boxes changes, set the default date
    // to the latest date in the series
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets date on source change; user also clicks plot
    setSelectedDate(getDefaultSelectedDate(selectedData, selectedTableSource));
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- only reset date when the dropdown changes, not on API refetches
  }, [selectedTableSource]);
  return (
    <Stack pb={50}>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <DateRangeSelector />
        {isAdmin ? <CostExcelButton /> : null}
      </Stack>
      <Paper
        elevation={0}
        sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, mt: 2 }}
      >
        <Stack spacing={2} mx={0} p={2}>
          <Stack direction="row" spacing={2} sx={{ alignItems: "flex-end" }}>
            {isAdmin ? (
              <ToggleSelector<CostTableSource>
                title={"plot_to_show"}
                selectedValue={selectedTableSource}
                setSelectedValue={setSelectedTableSource}
                availableValueLabels={availableTableSourceLabels}
              />
            ) : null}
            <ToggleSelector<MassDenominator>
              title={"per"}
              selectedValue={selectedMassDenominator}
              setSelectedValue={setSelectedMassDenominator}
              availableValueLabels={availableMassDenominatorLabels}
            />
            <MultiSelect<string, string>
              sx={{ minWidth: 240 }}
              options={availableCostSources}
              getOptionId={(source) => source}
              getOptionLabel={(source) => t(source)}
              values={selectedSources}
              setValues={setSelectedSources}
              small
              selectAll
              labelAbove
              label={t("sources")}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <CostPlot
              groupedBySourceCost={selectedData}
              selectedTableSource={selectedTableSource}
              selectedMassDenominator={selectedMassDenominator}
              selectedSources={visibleSources}
              setSelectedDate={setSelectedDate}
            />
            <CostTableList data={data} selectedDate={selectedDate} />
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

const getDefaultSelectedDate = (
  groupedBySourceCost: GroupedByCostSource,
  selectedTableSource: CostTableSource | null
): Date => {
  const defaultSelectedDateString = groupedBySourceCost.seriesCollection
    .find((series) => series.source == selectedTableSource)
    ?.items.map(({ date }) => new Date(date))
    .reduce(
      (max, current) => {
        return max === null || current > max ? current : max;
      },
      null as Date | null
    );
  return defaultSelectedDateString
    ? new Date(defaultSelectedDateString)
    : new Date();
};

const CostExcelButton = () => {
  const { startDate, endDate } = useDateRange();
  const tenantName = useTenant();
  const [trigger] =
    backendApi.endpoints.getStrategyDashboardCost.useLazyQuery();
  const start = startDate.format();
  const end = endDate.format();
  const getBlob = () =>
    downloadAsExcel(trigger, {
      tenantName,
      start,
      end,
      timeAggregation: "monthly",
      "Cache-Control": "no-cache",
    });
  return <ExcelDownloadButton variant="outlined" getBlob={getBlob} />;
};
